/* The filter band: a pinned search slot, one row of chips, and the chrome rules
 * around it.
 *
 * Lives in component/ because Library, Index and Map all need the same band —
 * three drifted copies with three different insets is what this replaces.
 * Presentational: the caller owns the selection and the collapsed state, exactly
 * as with g-chips and g-sheet. The one thing the band owns is whether its search
 * field is open, because nothing outside it can answer that.
 *
 * Two substrates (ADR 0017 §1), because chrome treatment follows what is under it:
 *
 *   document  opaque and sticky; content scrolls under it
 *   canvas    a transparent strip with opaque pills, over a full-bleed map
 *   overlay   opaque like the first, placed like the second — Map's place list
 *
 * The pills' top edge sits --band-inset below the top of the VISIBLE viewport on
 * every surface. The substrates reach that line from opposite directions: the
 * outlet pads a document workspace by --safe-t, while Map cancels that padding so
 * the map can bleed, and therefore pays it back here. --safe-t is 0px on the
 * reference device, so getting this wrong is invisible except on a notched or
 * gesture-nav phone.
 *
 * Search does NOT own the query. It reuses g-field, whose debounced `g-input`
 * bubbles composed all the way to the shell — the same event the desktop header
 * sends. The shell stays the single owner (ADR 0017 §10), so a search here and a
 * search up there cannot disagree.
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query as domQuery } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import type { ChipOption } from '../g-chips/g-chips.js';
import type { GField } from '../g-field/g-field.js';
import '../g-chips/g-chips.js';
import '../g-field/g-field.js';
import '../g-icon/g-icon.js';

@customElement('g-filter-bar')
export class GFilterBar extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        /* Viewport top to pill top. The one number behind the baseline. */
        --band-inset: var(--space-3);
        display: flex;
        align-items: center;
        gap: var(--space-2);
        position: sticky;
        top: 0;
        z-index: 3;
        /* Padding insets the contents; the border still spans the full width. */
        padding: var(--band-inset) var(--content-r) var(--band-inset) var(--content-l);
        border-bottom: 1px solid var(--border);
        background: var(--bg);
        /* Asymmetric, because the two directions mean different things. Arriving
           is a request — you swiped down to reach the filters — so it is quick and
           decelerates into place. Leaving is not, so it accelerates away.

           The transition lives on the DESTINATION state: a transition into
           [collapsed] is governed by the rule below, out of it by this one. */
        transition: transform 150ms cubic-bezier(0, 0, 0.2, 1);
      }
      /* g-chips reserves 4px of hit-area extension inside its own host below the
         breakpoint, so the band pays the remainder rather than stacking a second
         helping on top. Pills end up --band-inset from both of the band's edges. */
      @media (pointer: coarse) and (max-width: 959px) {
        :host {
          padding-block: calc(var(--band-inset) - 4px);
        }
      }
      /* Translated, never collapsed: a height animation would relayout the list
         every frame, and content must not shift up or the list double-scrolls.
         Still in the accessibility tree — a filter a screen reader cannot reach is
         not a filter, so this is never display: none. */
      :host([collapsed]) {
        transform: translateY(-100%);
        transition: transform 200ms cubic-bezier(0.4, 0, 1, 1);
      }
      /* An open field outranks the scroll rule. The caller cannot know the field
         is up, so the band refuses rather than asking to be excused: hiding a
         focused input while its own results scroll under it is indefensible. */
      :host([collapsed][searching]) {
        transform: none;
      }
      /* The document-level reduced-motion rule cannot cross this shadow root. */
      @media (prefers-reduced-motion: reduce) {
        :host {
          transition: none;
        }
      }
      /* Holds the band's height through the moment between a new query and its
         facets arriving, when there are legitimately no chips to draw — otherwise
         the band collapses and the content jumps on every search. 28px is a
         desktop chip: 0.82rem at line-height 1.5, plus 5px padding and 1px border. */
      g-chips {
        flex: 1;
        min-width: 0;
        min-height: 28px;
      }
      /* The touch figure is the real one: a 36px pill inside g-chips' own 4px of
         hit-area extension. Stating 28 here too would shrink the band by 8px
         whenever the chips are momentarily absent, which is the jump this guards. */
      @media (pointer: coarse) and (max-width: 959px) {
        g-chips {
          min-height: 44px;
        }
      }

      /* ── the pinned search slot ──────────────────────────────
         Pinned and OUTSIDE the scrolling row, deliberately: a search control that
         can scroll out of reach makes the primary way into a workspace
         unreachable (ADR 0017 §10). */
      .lead {
        display: inline-flex;
        position: relative;
        flex: none;
        align-items: center;
        min-height: 36px;
        border: 1px solid var(--border);
        border-radius: var(--radius-full);
        background: var(--surface);
        color: var(--text-dim);
        font-family: var(--font-ui);
        font-size: 0.82rem;
        cursor: pointer;
      }
      /* The header owns search at desktop widths, so this slot is the mobile
         replacement for it and not a second way in. Library and Index keep their
         band above the breakpoint, and both entry points were live there at once —
         the header showing the text while the band showed the pill.

         Width, not pointer, to complement g-shell's one layout swap exactly; the
         two gates have to stay each other's inverse. display: none so the
         duplicate leaves the accessibility tree too, the same reason the shell
         gives for hiding the inactive nav. BP_DESKTOP (960), mirrored from
         design/tokens.ts because a media query cannot read a custom property.
         Map is unaffected either way: it only builds a band while narrow. */
      @media (min-width: 960px) {
        .lead {
          display: none;
        }
      }
      /* 36px of paint, 44px of hit area — the same split the chips use, and the
         same reason: a filter row should not be the heaviest thing on the screen.

         On the BUTTONS, never on the container. With a query set the container is a
         div wrapping two buttons, and an extension on it would paint over both and
         swallow their taps — measured on the device, neither the label nor the ✕
         responded while the icon-only state worked, because there the container IS
         the button. inset is vertical-only, so neighbouring extensions meet rather
         than overlap. */
      @media (pointer: coarse) and (max-width: 959px) {
        button.lead::after,
        .q-open::after,
        .q-clear::after {
          content: '';
          position: absolute;
          inset: -4px 0;
        }
      }
      /* Guarded: on touch a hover latches and outlives the tap (see g-chips). */
      @media (hover: hover) {
        .lead:hover {
          border-color: var(--accent);
          color: var(--text);
        }
      }
      /* Icon-only, when there is no query to show. Square, so it reads as a
         control rather than as a chip with a missing label. */
      button.lead {
        justify-content: center;
        width: 36px;
        padding: 0;
      }
      button.lead:active {
        background: color-mix(in oklab, var(--text) 10%, var(--surface));
      }
      /* With a query set the slot becomes the query itself: one element carrying
         the fact that a search is active, and the only place to end it. It earns
         its keep most over the map, where a filtered set of pins otherwise has an
         invisible cause. */
      .lead.q {
        border-color: var(--accent);
        background: color-mix(in oklab, var(--accent) 16%, var(--surface));
        color: var(--text);
      }
      .q-open,
      .q-clear {
        display: inline-flex;
        position: relative;
        align-items: center;
        gap: var(--space-1);
        min-height: 34px;
        padding: 0 var(--space-2);
        border: 0;
        background: none;
        color: inherit;
        font: inherit;
        cursor: pointer;
      }
      /* These two sit inside the .lead pill, which already owns an opaque
         background — so the translucent wash composites over it and needs no mix
         of its own, unlike button.lead above. */
      .q-open:active,
      .q-clear:active {
        background: var(--press);
      }
      .q-open {
        padding-left: var(--space-3);
        font-weight: 700;
      }
      /* Truncated rather than allowed to push the chips off the row: the query is
         an identifier here, not the content. */
      .q-text {
        overflow: hidden;
        max-width: 92px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .q-clear {
        padding-right: var(--space-3);
        border-left: 1px solid color-mix(in oklab, var(--accent) 35%, transparent);
        color: var(--text-dim);
      }
      @media (hover: hover) {
        .q-clear:hover {
          color: var(--text);
        }
      }
      .lead g-icon {
        --icon-size: 15px;
      }
      .q-open g-icon {
        color: var(--accent);
      }

      /* ── the open field ─────────────────────────────────────
         g-field brings the debounce, the clear button and the 16px font that stops
         iOS zooming a standalone PWA on focus. The whole search affordance lives
         inside its one pill — back slotted into the leading position, ✕ at the
         trailing edge — so nothing floats alongside it. */
      /* Matched to g-chips' own height so the band does not change size when the
         field opens — g-field's pill is 36px where the chip row reserves 44. The
         centring is what keeps the 36px pill on the same baseline as the chips it
         replaces, rather than riding to the top of the taller row. */
      g-field {
        flex: 1;
        min-width: 0;
        align-items: center;
        min-height: 28px;
      }
      @media (pointer: coarse) and (max-width: 959px) {
        g-field {
          min-height: 44px;
        }
      }
      /* Slotted INTO g-field's leading position, so it sits inside the pill where
         the search glyph would otherwise be — one self-contained search affordance
         rather than a control floating beside it. Styled here because the element
         belongs to this shadow root even though it renders in g-field's. */
      .back {
        display: inline-flex;
        position: relative;
        flex: none;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        border: 0;
        border-radius: var(--radius-full);
        background: none;
        color: var(--text);
        cursor: pointer;
      }
      .back:active {
        background: var(--press);
      }
      .back g-icon {
        --icon-size: 18px;
      }
      /* 28px of paint inside a 36px pill, extended vertically to the 44px floor.
         Vertical only — growing sideways would take taps from the caret. */
      @media (pointer: coarse) and (max-width: 959px) {
        .back::after {
          content: '';
          position: absolute;
          inset: -8px 0;
        }
      }

      /* Over a viewport rather than a document: no block, no divider, and the
         strip is click-through so the map still takes a drag started on it. Only
         the controls answer the pointer. */
      :host([substrate='canvas']) {
        position: absolute;
        top: var(--safe-t);
        right: 0;
        left: 0;
        z-index: 1;
        border-bottom: 0;
        background: none;
        pointer-events: none;
      }
      /* Over the mobile place list: placed like the canvas variant so it holds the
         same position across a view swap and only the background changes, but
         opaque and divided, because rows scroll under it rather than a map. */
      :host([substrate='overlay']) {
        position: absolute;
        top: var(--safe-t);
        right: 0;
        left: 0;
        z-index: 2;
      }
      /* Both placed variants clear the status bar as well, so nothing lingers in
         the strip above them. */
      :host([substrate='canvas'][collapsed]),
      :host([substrate='overlay'][collapsed]) {
        transform: translateY(calc(-100% - var(--safe-t)));
      }
      /* drop-shadow, not box-shadow: it has to follow each pill's own rounded
         outline, and the host here is the scrolling row. A pill's 1px border
         disappears over a dark park or a satellite tile. min-width lets the row
         shrink and scroll rather than push anything off the map. */
      :host([substrate='canvas']) g-chips {
        min-width: 0;
        pointer-events: auto;
        filter: drop-shadow(0 3px 10px rgb(0 0 0 / 0.3));
      }
      :host([substrate='canvas']) .lead,
      :host([substrate='canvas']) g-field {
        pointer-events: auto;
        filter: drop-shadow(0 3px 10px rgb(0 0 0 / 0.3));
      }
    `,
  ];

  @property({ attribute: false }) options: ChipOption[] = [];
  @property() selected = '';
  /** Which treatment to wear. See the substrates above. */
  @property({ reflect: true }) substrate: 'document' | 'canvas' | 'overlay' = 'document';
  /**
   * Out of view, because the caller is reading rather than filtering.
   *
   * Owned by the caller: only it knows which element scrolls. See
   * `core/scroll-direction.ts` for the hysteresis every caller shares.
   */
  @property({ type: Boolean, reflect: true }) collapsed = false;

  /** Whether this workspace has a search at all, from its own WorkspaceDef. */
  @property({ type: Boolean }) searchable = false;
  @property() placeholder = 'Search';
  /** The committed query, owned by the shell. Shown in the slot when set. */
  @property() query = '';
  /** Field open. Reflected so the collapse rule above can defer to it. */
  @property({ type: Boolean, reflect: true }) searching = false;

  @domQuery('g-field') private fieldEl?: GField;

  /**
   * Value bound into the field when it opens, and never updated while typing.
   *
   * Stable on purpose: Lit skips a binding whose value has not changed, so an
   * unrelated re-render mid-typing — Library rebuilds its chips on every
   * keystroke while facets are in flight — cannot reach in and reset the input
   * under the caret.
   */
  private draft = '';

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('popstate', this.onPopState);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.onPopState);
  }

  /** Open the field, prefilled with whatever query is already active. */
  openSearch(): void {
    this.draft = this.query;
    this.searching = true;
    this.markHistory();
    // Lets Map swap to the place list first: on the canvas a search's only visible
    // effect is that pins vanish, so there is nothing to read there (ADR 0017 §10).
    this.dispatchEvent(new CustomEvent('search-open', { bubbles: true, composed: true }));
    void this.updateComplete.then(() => this.focusField());
  }

  private focusField(): void {
    this.fieldEl?.renderRoot.querySelector('input')?.focus();
  }

  /**
   * Stand a history entry behind the open field, so the system back button closes it.
   *
   * **Pushed, never popped**, and the asymmetry is the whole design. Popping would
   * mean calling `history.back()` from the same tap that may also be navigating —
   * a tile, a venue row — and a queued traversal racing a synchronous `pushState`
   * is the exact hazard `Router.go` already carries a note about. Measured through
   * the logic: Detail would open and then immediately close itself. So the entry
   * is only ever consumed by a real back press.
   *
   * Guarded on the marker already being present, which bounds the cost: repeated
   * open/close cycles reuse one entry instead of stacking them, so at most one back
   * press is ever spent on an entry whose field has already gone.
   *
   * Android eats the first press with the keyboard, so this answers the second.
   * The four other exits (back control, keyboard key, ✕, tapping away) are what
   * make that acceptable rather than the only way out.
   */
  private markHistory(): void {
    if ((history.state as { bandSearch?: boolean } | null)?.bandSearch) return;
    // Deliberately not spread — see ADR 0021 decision 2. The band only exists in
    // a workspace, never over Detail.
    history.pushState({ bandSearch: true }, '');
  }

  private onPopState = (): void => {
    this.closeSearch();
  };

  /** Close the field. The query survives — it is the shell's, and the slot shows it. */
  closeSearch(): void {
    if (!this.searching) return;
    this.searching = false;
    // The band was pinned while the field was open, so the scroll that happened
    // underneath left a collapse pending. Ask to start over rather than vanish the
    // moment the field folds away — the query chip is the only thing saying a
    // search is active, and it has to be reachable.
    this.dispatchEvent(new CustomEvent('search-close', { bubbles: true, composed: true }));
  }

  private emitQuery(value: string): void {
    this.dispatchEvent(
      new CustomEvent('g-input', { detail: value, bubbles: true, composed: true }),
    );
  }

  /**
   * Cancel the search outright: drop the query and fold the field away.
   *
   * One meaning for the ✕ glyph wherever it appears on this band — in the open
   * field and on the query chip alike. "End the search but keep it" is what Back,
   * the keyboard key and tapping away are for.
   */
  private clear = (): void => {
    this.draft = '';
    this.emitQuery('');
    this.closeSearch();
  };

  /**
   * Hold focus through the tap, so `focusout` cannot close the field before the
   * click lands.
   *
   * Acting on `pointerdown` was the ghost-tap bug: closing there tears the field
   * out of the DOM while the finger is still down, and the click that follows
   * retargets to whichever chip has taken those pixels — selecting a filter nobody
   * asked for. `preventDefault` on pointerdown suppresses the focus change but NOT
   * the click (that would need `touchstart`), which is exactly why the stray click
   * still arrived. So: hold here, act on click, mutate the DOM afterwards.
   */
  private hold = (e: Event): void => {
    e.preventDefault();
  };

  /** Back: end the search and keep the query, which reappears as the chip. */
  private onBack = (): void => {
    this.closeSearch();
  };

  /**
   * g-field's own ✕ emptied the field, so the search is over.
   *
   * It has already emitted the undebounced empty `g-input` that clears the query —
   * this only folds the field away. Distinct from `g-input` reaching '' by
   * backspacing, which must NOT close anything, and that is exactly why g-field
   * raises a separate `g-clear`.
   */
  private onFieldClear = (): void => {
    this.draft = '';
    this.closeSearch();
  };

  /**
   * The keyboard's own search key means the same thing as Back — done entering,
   * keep what I typed. There is no form to submit; the query is already live.
   */
  private onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    this.closeSearch();
  };

  /**
   * Losing focus ends the search and leaves the query in the slot — tapping
   * anything else in the workspace commits rather than cancels.
   */
  private onBlur = (): void => {
    this.closeSearch();
  };

  private renderLead() {
    if (!this.query) {
      return html`<button class="lead" aria-label="Search" @click=${() => this.openSearch()}>
        <g-icon name="search"></g-icon>
      </button>`;
    }
    return html`<div class="lead q">
      <button class="q-open" aria-label=${`Edit search: ${this.query}`} @click=${() =>
        this.openSearch()}>
        <g-icon name="search"></g-icon><span class="q-text">${this.query}</span>
      </button>
      <button class="q-clear" aria-label="Clear search" @click=${this.clear}>
        <g-icon name="x"></g-icon>
      </button>
    </div>`;
  }

  /* g-chips renders unconditionally when not searching, even with nothing to draw:
     its min-height is what holds the band's height while facets are in flight. */
  override render() {
    if (this.searching) {
      return html`
        <g-field
          .value=${this.draft}
          placeholder=${this.placeholder}
          @keydown=${this.onKey}
          @focusout=${this.onBlur}
          @g-clear=${this.onFieldClear}
        >
          <button
            slot="lead"
            class="back"
            aria-label="End search"
            @pointerdown=${this.hold}
            @click=${this.onBack}
          >
            <g-icon name="back"></g-icon>
          </button>
        </g-field>
      `;
    }
    return html`
      ${this.searchable ? this.renderLead() : nothing}
      <g-chips .options=${this.options} .selected=${this.selected}></g-chips>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-filter-bar': GFilterBar;
  }
}
